import { RepeatedInstance } from 'FRONTEND/reducers/formSlice';

// Utility kept local: creates a hidden input appended for repeated instances.
const createHiddenInput = (inputName: string, value: string): HTMLInputElement => {
  const input = document.createElement('input');
  input.id = inputName;
  input.className = 'hidden';
  input.name = inputName;
  input.type = 'text';
  input.value = value;
  return input;
};

// Adds hidden inputs for all repeated instances stored in formSlice.
// Index increments per (examType + sectionId + fieldId) occurrence, regardless of uuid.
// Field naming mirrors the expected repeatable-fields convention:
// consultation-{examType}-{prefix}{index}{suffix}, where prefix is before first underscore and suffix includes it.
// TODO: submit the uuid instead of a sequential index, and have the backend store it as-is
// (see REPEATABLE_FIELD_ID_RE in parsys_cloud/helpers/exams_helpers.py). Indexes shift whenever
// an instance is inserted or removed, so they cannot reliably identify an instance across payloads.
export const addRepeatedInstancesToForm = (
  {
    form,
    instances,
  }: {
    form: HTMLFormElement;
    instances: RepeatedInstance[];
  },
): HTMLFormElement => {
  if (!instances?.length) {
    return form;
  }

  // sectionCounter counts how many distinct uuids have appeared per sectionId (how many times the section is repeated).
  // uuidIndexCache stores the assigned index for a given uuid
  // All fields sharing the same uuid reuse the same index.
  const sectionCounter: Record<string, number> = {};
  const indexByInstance: Record<string, number> = {};

  // A uuid represents one instance of a repeated section.
  instances.forEach(({ fieldId, sectionId, examType, uuid, value }) => {
    // In case a section has the same id in 2 different exam types, we differentiate them.
    const sectionKey = `${examType}__${sectionId}`;

    // Index will be the occurrence count of this uuid within its section group.
    let index = indexByInstance[uuid];
    if (!index) {
      sectionCounter[sectionKey] = (sectionCounter[sectionKey] || 0) + 1;
      index = sectionCounter[sectionKey];
      indexByInstance[uuid] = index;
    }

    // We insert the index into the field name at the first underscore position.
    const underscorePos = fieldId.indexOf('_');
    if (underscorePos === -1) {
      // No underscore found, skip (unexpected format).
      return;
    }
    const inputName = `consultation-${examType}-${fieldId.slice(0, underscorePos)}${index}${fieldId.slice(underscorePos)}`;

    form.appendChild(createHiddenInput(inputName, value));
  });

  return form;
};
